import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { td } from '@/lib/i18n';
import { toast } from 'sonner';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { TextField } from '@/components/text-field';
import { TextAreaField } from '@/components/textarea-field';
import { SelectField } from '@/components/select-field';
import { DateField } from '@/components/date-field';
import { FileDropZone } from '@/components/file-drop-zone';
import { driverSchema } from '@/schema/driver-schema';
import { postDriver, uploadDriverImage } from '@/services/driver';
import { errorHandler } from '@/lib/utils';
export const Route = createFileRoute('/console/_authenticated/_layout/internal/_layout/drivers/new')({
    component: NewDriverPage,
});
function NewDriverPage() {
    const { t } = useTranslation('internal');
    const navigate = Route.useNavigate();
    const [photoFile, setPhotoFile] = React.useState();
    const form = useForm({
        resolver: yupResolver(driverSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            phoneNumber: '',
            telegramHandle: '',
            vehicleType: 'tuk_tuk',
            licensePlate: '',
            payoutMethod: 'cash',
            payoutAccount: '',
            status: 'active',
            locale: 'km',
            joinedAt: new Date().toISOString().slice(0, 10),
            notes: '',
        },
    });
    const { mutateAsync: createDriver, isPending: isCreatePending } = useMutation({
        mutationFn: postDriver,
        onError: errorHandler,
    });
    const { mutateAsync: uploadPhoto, isPending: isUploadPending } = useMutation({
        mutationFn: ({ publicId, file }) => uploadDriverImage({ publicId, file }),
        onError: errorHandler,
    });
    const isPending = isCreatePending || isUploadPending;
    const handleSubmit = async (data) => {
        const driver = await createDriver(data);
        if (photoFile) {
            await uploadPhoto({ publicId: driver.publicId, file: photoFile });
        }
        toast.success(t('drivers.toast.created', { name: driver.fullName }));
        navigate({ to: '/console/internal/drivers' });
    };
    const vehicleOptions = ['tuk_tuk', 'motorbike', 'car', 'other'].map((value) => ({
        value,
        label: td(t, `enum.vehicle_type.${value}`),
    }));
    const payoutOptions = ['cash', 'aba_pay', 'wing', 'acleda_bank', 'other'].map((value) => ({
        value,
        label: td(t, `enum.payout_method.${value}`),
    }));
    const statusOptions = ['active', 'paused', 'inactive'].map((value) => ({
        value,
        label: td(t, `enum.driver_status.${value}`),
    }));
    // Khmer first, and the default: it is what almost every driver reads. The
    // labels are each written in their own language, so the staff member picking
    // one can match it against what the driver actually said.
    const localeOptions = ['km', 'en', 'fr'].map((value) => ({
        value,
        label: td(t, `enum.driver_locale.${value}`),
    }));
    return (<div className="p-6 max-w-3xl mx-auto space-y-4 w-full">
            <div className="space-y-1 border-b pb-4">
                <h1 className="text-3xl font-semibold">{t('drivers.page.new_title')}</h1>
                <p className="text-sm text-muted-foreground">{t('drivers.page.new_desc')}</p>
            </div>

            <Form {...form}>
                <Card>
                    <CardHeader>
                        <CardTitle>{t('drivers.section.identity')}</CardTitle>
                        <CardDescription>{t('drivers.section.identity_desc')}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <form className="grid gap-4 md:grid-cols-2" onSubmit={(e) => e.preventDefault()}>
                            <TextField control={form.control} name="firstName" label={t('drivers.field.first_name')} disabled={isPending}/>
                            <TextField control={form.control} name="lastName" label={t('drivers.field.last_name')} disabled={isPending}/>
                            <TextField control={form.control} name="phoneNumber" label={t('drivers.field.phone_number')} disabled={isPending}/>
                            <TextField control={form.control} name="telegramHandle" label={t('drivers.field.telegram_optional')} placeholder="@username" disabled={isPending}/>
                        </form>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('drivers.section.vehicle')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                            <SelectField control={form.control} name="vehicleType" label={t('drivers.field.vehicle_type')} options={vehicleOptions} disabled={isPending}/>
                            <TextField control={form.control} name="licensePlate" label={t('drivers.field.license_plate')} disabled={isPending}/>
                        </div>
                        <div className="space-y-2">
                            <p className="text-sm font-medium">{t('drivers.section.vehicle_photo')}</p>
                            <FileDropZone images={photoFile ? [photoFile] : []} onDrop={(files) => setPhotoFile(files[0])} onUpload={(e) => e.target.files && setPhotoFile(e.target.files[0])} onRemove={() => setPhotoFile(undefined)}/>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('drivers.section.payout')}</CardTitle>
                        <CardDescription>{t('drivers.section.payout_desc')}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                            <SelectField control={form.control} name="payoutMethod" label={t('drivers.field.payout_method')} options={payoutOptions} disabled={isPending}/>
                            <TextField control={form.control} name="payoutAccount" label={t('drivers.field.payout_account')} disabled={isPending}/>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('drivers.section.other')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                            <SelectField control={form.control} name="status" label={t('drivers.section.status')} options={statusOptions} disabled={isPending}/>
                            <DateField control={form.control} name="joinedAt" label={t('drivers.field.joined_on')} disabled={isPending}/>
                            <SelectField control={form.control} name="locale" label={t('drivers.field.language')} description={t('drivers.field.language_help')} options={localeOptions} disabled={isPending}/>
                        </div>
                        <TextAreaField control={form.control} name="notes" label={t('drivers.field.notes')} disabled={isPending}/>
                    </CardContent>
                </Card>

                <div className="pt-4">
                    <Button size="lg" className="w-full" onClick={form.handleSubmit(handleSubmit)} disabled={isPending}>
                        <CheckCircle className="h-4 w-4"/>
                        {t('drivers.action.create')}
                    </Button>
                </div>
            </Form>
        </div>);
}
